import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

interface CountdownTimerProps {
  targetDate: string;
  targetTime?: string;
  title?: string;
}

const CountdownContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  borderRadius: '20px',
  padding: '48px 24px',
  boxShadow: '0 4px 20px rgba(69, 91, 64, 0.25)',
  textAlign: 'center',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
    zIndex: 0,
  },
  '& > *': {
    position: 'relative',
    zIndex: 1,
  },
}));

const CountdownTitle = styled(Typography)(({ theme }) => ({
  fontFamily: '"Lora", serif',
  fontSize: '24px',
  fontWeight: 700,
  color: '#ffffff',
  marginBottom: '32px',
  textTransform: 'uppercase',
  letterSpacing: '1px',
  [theme.breakpoints.down('md')]: {
    fontSize: '20px',
    marginBottom: '24px',
  },
}));

const TimeGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gap: '24px',
  maxWidth: '600px',
  margin: '0 auto',
  [theme.breakpoints.down('md')]: {
    gap: '16px',
    maxWidth: '500px',
  },
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '16px',
    maxWidth: '300px',
  },
}));

const TimeUnit = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(255, 255, 255, 0.15)',
  borderRadius: '16px',
  padding: '24px 16px',
  minHeight: '120px',
  position: 'relative',
  overflow: 'hidden',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05))',
    borderRadius: '16px',
  },
  [theme.breakpoints.down('md')]: {
    padding: '20px 12px',
    minHeight: '100px',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '16px 8px',
    minHeight: '80px',
  },
}));

const TimeValue = styled(Typography)(({ theme }) => ({
  fontFamily: '"Lora", serif',
  fontSize: '32px',
  fontWeight: 700,
  color: '#ffffff',
  lineHeight: 1,
  position: 'relative',
  zIndex: 1,
  [theme.breakpoints.down('sm')]: {
    fontSize: '24px',
  },
}));

const TimeLabel = styled(Typography)(({ theme }) => ({
  fontFamily: '"Lora", serif',
  fontSize: '12px',
  fontWeight: 600,
  color: '#ffffff',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  marginTop: '8px',
  position: 'relative',
  zIndex: 1,
  opacity: 0.9,
  [theme.breakpoints.down('sm')]: {
    fontSize: '10px',
  },
}));

const ExpiredMessage = styled(Typography)(({ theme }) => ({
  fontFamily: '"Lora", serif',
  fontSize: '24px',
  fontWeight: 700,
  color: theme.palette.primary.main,
  textAlign: 'center',
  padding: '40px 20px',
}));

const Separator = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  right: '-8px',
  transform: 'translateY(-50%)',
  fontSize: '24px',
  fontWeight: 700,
  color: theme.palette.primary.main,
  zIndex: 2,
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}));

const CountdownTimer: React.FC<CountdownTimerProps> = ({ 
  targetDate, 
  targetTime = "00:00",
  title = "TIEMPO HASTA EL DÍA 1" 
}) => {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    const calculateTimeLeft = () => {
      // Convert 12-hour time to 24-hour format
      const convertTo24Hour = (time12h: string): string => {
        const [time, modifier] = time12h.split(' ');
        let [hours, minutes] = time.split(':');
        if (hours === '12') {
          hours = '00';
        }
        if (modifier === 'PM') {
          hours = parseInt(hours, 10) + 12 + '';
        }
        return `${hours.padStart(2, '0')}:${minutes}:00`;
      };

      // Combine date and time
      const time24h = convertTo24Hour(targetTime);
      const dateTimeString = `${targetDate}T${time24h}`;
      
      // Create target date in Spanish timezone
      const target = new Date(dateTimeString);
      
      // Get current time in Spanish timezone
      const nowUTC = new Date();
      const nowSpain = new Date(nowUTC.toLocaleString("sv-SE", {timeZone: "Europe/Madrid"}));
      
      // Calculate difference
      const difference = target.getTime() - nowSpain.getTime();

      if (difference > 0 && !isNaN(target.getTime())) {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);

        setTimeLeft({ days, hours, minutes, seconds });
        setIsExpired(false);
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        setIsExpired(true);
      }
    };

    // Calculate immediately
    calculateTimeLeft();

    // Set up interval
    const interval = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(interval);
  }, [targetDate, targetTime]);

  const padZero = (num: number): string => {
    return num.toString().padStart(2, '0');
  };

  if (isExpired) {
    return (
      <CountdownContainer>
        <ExpiredMessage>
          ¡Ha llegado el momento! 🌱
        </ExpiredMessage>
      </CountdownContainer>
    );
  }

  return (
    <CountdownContainer>
      <CountdownTitle>{title}</CountdownTitle>
      <TimeGrid>
        <Box sx={{ position: 'relative' }}>
          <TimeUnit>
            <TimeValue>{padZero(timeLeft.days)}</TimeValue>
            <TimeLabel>Días</TimeLabel>
          </TimeUnit>
          <Separator>:</Separator>
        </Box>
        <Box sx={{ position: 'relative' }}>
          <TimeUnit>
            <TimeValue>{padZero(timeLeft.hours)}</TimeValue>
            <TimeLabel>Horas</TimeLabel>
          </TimeUnit>
          <Separator>:</Separator>
        </Box>
        <Box sx={{ position: 'relative' }}>
          <TimeUnit>
            <TimeValue>{padZero(timeLeft.minutes)}</TimeValue>
            <TimeLabel>Minutos</TimeLabel>
          </TimeUnit>
          <Separator>:</Separator>
        </Box>
        <TimeUnit>
          <TimeValue>{padZero(timeLeft.seconds)}</TimeValue>
          <TimeLabel>Segundos</TimeLabel>
        </TimeUnit>
      </TimeGrid>
    </CountdownContainer>
  );
};

export default CountdownTimer;